export const DEFAULT_CERTIFICATION_TEXT =
	'I certify that I have seen the original document and that this is a true and complete copy of it.';

export const MAX_CERTIFICATION_TEXT_LENGTH = 1200;

export interface KycCertificationRequirementFlags {
	isProofOfIdentityCertificationRequired: boolean;
	isProofOfAddressCertificationRequired: boolean;
	isProofOfInvestmentVehicleCertificationRequired: boolean;
}

/** The proof flags a certification flag hangs off. */
export interface KycProofRequirementFlags {
	isProofOfIdentityRequired: boolean;
	isProofOfAddressRequired: boolean;
	isProofOfInvestmentVehicleRequired: boolean;
}

/** True when any proof type requires a certified copy. */
export const isAnyCertificationRequired = (
	flags: Partial<KycCertificationRequirementFlags> | null | undefined
): boolean =>
	!!flags &&
	(!!flags.isProofOfIdentityCertificationRequired ||
		!!flags.isProofOfAddressCertificationRequired ||
		!!flags.isProofOfInvestmentVehicleCertificationRequired);

/** True when proof of identity or proof of address requires a certified copy. */
export const isPersonalProofCertificationRequired = (
	flags: Partial<KycCertificationRequirementFlags> | null | undefined
): boolean =>
	!!flags &&
	(!!flags.isProofOfIdentityCertificationRequired ||
		!!flags.isProofOfAddressCertificationRequired);

export type KycCertificationFlag = keyof KycCertificationRequirementFlags;
export type KycProofFlag = keyof KycProofRequirementFlags;

/** The certification flag each proof flag owns. */
export const CERTIFICATION_FLAG_BY_PROOF: Record<KycProofFlag, KycCertificationFlag> = {
	isProofOfIdentityRequired: 'isProofOfIdentityCertificationRequired',
	isProofOfAddressRequired: 'isProofOfAddressCertificationRequired',
	isProofOfInvestmentVehicleRequired: 'isProofOfInvestmentVehicleCertificationRequired',
};

/**
 * The flags to write when a proof toggle moves. A certification is meaningless without the proof it
 * certifies, so switching a proof off switches it off. Switching a proof on leaves it alone —
 * certification is opt-in. Mirrors `resolveOne` in the kycSetting repository, which enforces the
 * same invariant on write.
 */
export const proofToggleFlags = (
	flag: KycProofFlag,
	required: boolean
): Partial<KycProofRequirementFlags & KycCertificationRequirementFlags> =>
	required ? { [flag]: true } : { [flag]: false, [CERTIFICATION_FLAG_BY_PROOF[flag]]: false };
